import { useState } from 'react';
import { Input, Upload, Radio, Row, Select, Tag, Form } from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useCountryArray } from '../../../../utils/countryArray';
import { useCountryCodeInfos } from '../../../owners/form/utils/useCountryCodeInfos';
import { createPhoneNumberFieldConfig } from '../../../../utils/formFields/phoneNumberField';

const { Dragger } = Upload;
const { Option } = Select;
const { TextArea } = Input;

const ANIMAL_TYPES = ['DOG', 'CAT', 'BIRD', 'EQUINE', 'FARM'];

/**
 * Custom hook that returns an array of fields for the "Other Infos" section in the user's profile form.
 * @hook
 *
 * @param {Object} options - The options object.
 * @param {Function} options.t - The translation function.
 * @param {Function} options.setDocumentsFileList - The function to set the documents file list.
 * @param {Object} options.documentsFileList - The documents file list.
 * @param {Function} options.setCountry - The function to set the country.
 * @param {string} options.country - The country.
 * @param {Object} options.form - The form object.
 * @param {boolean} options.loading - The loading state.
 * @param {boolean} options.isAnimals - Whether the pensioner has animals.
 * @param {Function} options.setIsAnimals - The function to set the isAnimals state.
 * @returns {Object} - The otherInfosFields array.
 */
export const useOtherInfosFields = ({
  t,
  setDocumentsFileList,
  documentsFileList,
  setCountry,
  country,
  form,
  loading
}) => {
  const countries = useCountryArray();
  const { handleCountryCodeInfos } = useCountryCodeInfos(country);

  const [cityOptions, setCityOptions] = useState([]);

  const isAnimals = Form.useWatch(['is_animals'], form);
  const movingWithAnimal = Form.useWatch(['moving_with_animal'], form);

  const filterOption = (input, option) =>
    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  const getRadioGroup = (onChange) => (
    <Radio.Group onChange={onChange}>
      <Radio value>{t('pensioners.form.yes')}</Radio>
      <Radio value={false}>{t('pensioners.form.no')}</Radio>
    </Radio.Group>
  );

  const draggerProps = (name) => ({
    onRemove: () => {
      const updatedFileList = { ...documentsFileList };
      delete updatedFileList[name];
      setDocumentsFileList(updatedFileList);
    },
    beforeUpload: (file) => {
      setDocumentsFileList((prevFileList) => ({
        ...prevFileList,
        [name]: [file]
      }));
      return false;
    },
    fileList: documentsFileList[name]
  });

  const otherInfosFields = [
    {
      name: ['address', 'number'],
      label: 'address.number'
    },
    {
      name: ['address', 'street']
    },
    {
      name: ['address', 'country'],
      input: (
        <Select
          showSearch
          filterOption={filterOption}
          isFieldsLoading={loading}
          onChange={(value) => {
            setCountry(value);
            form.setFieldValue(['address', 'postal_code'], undefined);
            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);
          }}
        >
          {(countries || []).map((value) => (
            <Option key={value} value={value}>
              <Tag>{value}</Tag>
            </Option>
          ))}
        </Select>
      ),
      rules: [{ required: true }]
    },
    {
      name: ['address', 'postal_code'],
      input: (
        <Input
          onChange={(e) => {
            const postalCode = e.target.value;
            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);

            if (postalCode.length < 5) return;

            handleCountryCodeInfos(postalCode).then(
              ({ cities, city, state }) => {
                form.setFieldValue(['address', 'state'], state || undefined);

                if (cities.length > 1) {
                  setCityOptions(cities);
                } else {
                  form.setFieldValue(['address', 'city'], city || undefined);
                  setCityOptions([]);
                }
              }
            );
          }}
        />
      )
    },
    {
      label: 'address.state',
      name: ['address', 'state'],
      rules: [{ required: true }]
    },
    {
      name: ['address', 'city'],
      input:
        cityOptions.length > 1 ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {cityOptions.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        ) : (
          <Input />
        )
    },
    createPhoneNumberFieldConfig('phone_number', []),
    {
      name: ['email']
    },
    {
      name: ['is_animals'],
      input: getRadioGroup((e) => {
        if (!e.target.value) {
          form.setFieldValue(['liked_animals_types'], undefined);
        }
      })
    },
    ...(isAnimals
      ? [
          {
            name: ['liked_animals_types'],
            rules: [{ required: true }],
            input: (
              <Select mode="multiple" allowClear>
                {ANIMAL_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    <Tag>{t(`animals.tags.${type}`)}</Tag>
                  </Option>
                ))}
              </Select>
            )
          }
        ]
      : []),
    {
      name: ['moving_with_animal'],
      input: getRadioGroup((e) => {
        if (!e.target.value) {
          form.setFieldValue(['animals_experiences'], undefined);
        }
      })
    },
    ...(movingWithAnimal
      ? [
          {
            name: ['animals_experiences'],
            rules: [{ required: true }],
            input: (
              <TextArea
                rows={4}
                value={(() => {
                  const val = form.getFieldValue(['animals_experiences']);
                  if (Array.isArray(val)) return val.join(', ');
                  return val;
                })()}
                onChange={(e) => {
                  form.setFieldValue(['animals_experiences'], [e.target.value]);
                }}
              />
            )
          }
        ]
      : []),
    {
      name: ['is_smoker'],
      input: getRadioGroup()
    },
    {
      label: 'pensioner_certificate',
      input: (
        <Dragger {...draggerProps('early_retirement_certificate')}>
          <UploadOutlined />
          {t('users.form.upload_file')}
        </Dragger>
      )
    },
    {
      label: 'insurance_certificate',
      input: (
        <Dragger {...draggerProps('insurance_certificate')}>
          <UploadOutlined />
          {t('users.form.upload_file')}
        </Dragger>
      )
    },
    {
      label: 'proof_of_address',
      input: (
        <Dragger {...draggerProps('residence_proof')}>
          <UploadOutlined />
          {t('users.form.upload_file')}
        </Dragger>
      )
    },
    {
      label: 'other_documents',
      input: (
        <Dragger {...draggerProps('other_documents')}>
          <Row justify="center">
            <InboxOutlined style={{ fontSize: 40 }} />
          </Row>
          <Row justify="center">{t('users.form.upload_other')}</Row>
        </Dragger>
      )
    }
  ];

  return { otherInfosFields };
};
